import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	Input,
	Modal,
	Row
} from 'reactstrap'
import { SpinnerComponent } from 'react-element-spinner'

import { trigger } from '../../events/customEvents'
import AuctionTabsService from '../../services/AuctionTabsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'

/**
 * CounterOfferModal - Modal for entering counter amount.
 * BRD: "Counter – Offer the highest bidder the chance to purchase at a counter
 * amount which would be higher than their high bid and equal to or less than the reserve"
 */
const CounterOfferModal = ({ isOpen = false, auction = null, counterAtReserve = false, onClose, onSuccess }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [counterAmount, setCounterAmount] = useState('')

	// Pre-populate counter amount when counterAtReserve is true
	useEffect(() =>
	{
		if (isOpen && counterAtReserve && auction?.reservePrice)
		{
			setCounterAmount(auction.reservePrice.toString())
		}
		else if (isOpen)
		{
			setCounterAmount('')
		}
	}, [isOpen, counterAtReserve, auction?.reservePrice])

	const handleCancelModal = () =>
	{
		resetForm()
		if (typeof onClose === 'function')
		{
			onClose()
		}
	}

	const resetForm = () =>
	{
		setCounterAmount('')
	}

	const validateCounterAmount = () =>
	{
		const amount = parseFloat(counterAmount)
		const highBid = auction?.highBid || auction?.currentHighBid || 0
		const reserve = auction?.reservePrice || 0

		if (isNaN(amount) || amount <= 0)
		{
			return 'Please enter a valid counter amount.'
		}
		if (amount <= highBid)
		{
			return `Counter amount must be higher than the current high bid ($${highBid.toLocaleString()}).`
		}
		if (reserve > 0 && amount > reserve)
		{
			return `Counter amount cannot exceed the reserve price ($${reserve.toLocaleString()}).`
		}
		return null
	}

	const handleSendCounter = () =>
	{
		const validationError = validateCounterAmount()
		if (validationError)
		{
			trigger('raiseNotification', { title: 'Validation Error', message: validationError, type: 'warning', duration: 5 })
			return
		}

		setIsModalBusy(true)
		const amount = parseFloat(counterAmount)
		const bidId = auction?.highBidAuctionTierMemberBidId || auction?.highBidId || auction?.auctionTierMemberBidId

		AuctionTabsService
			.sendCounterOffer(auction.auctionId, bidId, amount, 24, '', sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsModalBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { 
					title: 'Counter Offer Sent', 
					message: `Counter offer of $${amount.toLocaleString()} has been sent to the high bidder.`, 
					type: 'success', 
					duration: 5 
				})
				resetForm()
				if (typeof onSuccess === 'function')
				{
					onSuccess()
				}
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}

	// Get VIN and reserve for BRD mockup format
	const vin = auction?.vin || auction?.vehicleVin || ''
	const reserve = auction?.reservePrice || 0
	
	const highBid = auction?.highBid ?? auction?.currentHighBid ?? auction?.highestBidAmount ?? null

	return (
		<Modal
			className="modal-dialog-centered"
			size="md"
			isOpen={isOpen}
		>
			<SpinnerComponent
				loading={isModalBusy}
				position="centered"
				message="Sending counter offer..."
			/>
			<Card className="mb-0">
				<CardBody>
					{/* BRD: Title shows VIN */}
					<div className="text-muted mb-3">
						<strong>VIN:{vin} has a bid on it</strong>
					</div>
					
					{/* QA Feedback: show the current high bid so it is visible while countering */}
					<div className="mb-3">
						<label className="mb-0 me-2"><strong>High Bid:</strong></label>
						<span>{ highBid != null ? `$${Number(highBid).toLocaleString()}` : 'N/A' }</span>
					</div>

					{/* BRD: Counter amount input or Counter at reserve display */}
					<div className="mb-4">
						{counterAtReserve ? (
							<div className="bg-light p-2 rounded">
								Counter at reserve: ${reserve.toLocaleString()}
							</div>
						) : (
							<div>
								<label className="mb-1">Counter amount:</label>
								<Input
									type="number"
									value={counterAmount}
									onChange={(e) => setCounterAmount(e.target.value)}
									placeholder="Enter counter amount"
								/>
							</div>
						)}
					</div>

					<Row>
						<Col className="text-center">
							<Button
								color="primary"
								className="me-2"
								onClick={handleSendCounter}
								disabled={!counterAtReserve && !counterAmount}
							>
								YES
							</Button>
							<Button
								color="secondary"
								onClick={handleCancelModal}
							>
								Cancel
							</Button>
						</Col>
					</Row>
				</CardBody>
			</Card>
		</Modal>
	)
}

export default CounterOfferModal
